/* eslint-disable react-hooks/exhaustive-deps */
import { useQuery, useMutation } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, withRouter } from "react-router-dom";

import { getArrangedUselimits } from "./utils";
import { PageTitle } from "../../components/Common/PageTitle";
import FunctionIndividualSettingList from "../../components/FunctionManagementPage/IndividualSettingList";
import { ModalEditUseLimit } from "../../components/FunctionManagementPage/ModalEditUseLimit";
import ModalFilterCondition from "../../components/FunctionManagementPage/ModalFilterCondition";
import { ModalConfirm } from "../../components/ModalConfirm";
import Tabs from "../../components/Tabs";
import { ORDER } from "../../config";
import { canUseBooleanData } from "../../models/featureUseLimits/FeatureUselimitFilterConditionData";
import { ArrangedUseLimitsData } from "../../models/featureUseLimits/FeatureUseLimitsData";
import { FeatureTypes } from "../../models/featureUseLimits/FeatureUseLimitsData.const";
import DataSvc from "../../services/dataSvc";
import "./styles.scss";

export type UseLimitsSortOrder = "asc" | "desc";

export interface IUseLimitsFilterFormData {
  searchKeyword: string;
  numberPerPage: number;
  pageIndex: number;
  useLimitsStatus: canUseBooleanData[];
  sort: string;
  order: UseLimitsSortOrder;
}

// The name/email column is backed by a joined association, so the field sent
// to the API differs per target type.
const NAME_SORT_FIELD_BY_TARGET: Record<FeatureTypes, string> = {
  [FeatureTypes.ORG]: "target_org.name",
  [FeatureTypes.LOC]: "target_loc.name",
  [FeatureTypes.USER]: "target_user.email",
};

type IFunctionManagementPageProps = RouteComponentProps<any>;

const FunctionManagementPage: React.FunctionComponent<
  IFunctionManagementPageProps
> = () => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`functionManagementPage.${key}`);

  const [targetType, setTargetType] = useState<FeatureTypes>(FeatureTypes.ORG);
  const [assignUseLimitData, setAssignUseLimitData] = useState<any>();
  const [showModalEditFeatures, setShowModalEditFeatures] =
    useState<boolean>(false);
  const [showModalConfirmDeleteUseLimits, setShowModalConfirmDeleteUseLimits] =
    useState<boolean>(false);
  const [
    showModalConfirmClearFilterCondition,
    setShowModalConfirmClearFilterCondition,
  ] = useState<boolean>(false);
  const [shownModalFilterCondition, setShownModalFilterCondition] =
    useState<boolean>(false);

  const defaultFilterFormData: IUseLimitsFilterFormData = {
    searchKeyword: "",
    numberPerPage: 20,
    pageIndex: 1,
    useLimitsStatus: [],
    sort: "name",
    order: ORDER.ASC as UseLimitsSortOrder,
  };

  const [filterFormData, setFilterFormData] =
    useState<IUseLimitsFilterFormData>(defaultFilterFormData);

  const { data: featuresData } = useQuery(["features", targetType], () => {
    return DataSvc.getFeatures({ target: targetType });
  });

  useEffect(() => {
    if (featuresData) {
      setFilterFormData({
        ...defaultFilterFormData,
        useLimitsStatus: featuresData
          .filter((item: any) => !item.is_metered)
          .map((feature: any) => {
            return { id: feature.id, can_use: undefined, name: feature.name };
          }),
      });
    }
  }, [featuresData]);

  const resetFilterFormData = () => {
    setFilterFormData({
      ...defaultFilterFormData,
      useLimitsStatus: featuresData
        .filter((item: any) => !item.is_metered)
        .map((feature: any) => {
          return { id: feature.id, can_use: undefined, name: feature.name };
        }),
    });
  };

  const { data: useLimitsData, refetch: refetchingUseLimits } = useQuery(
    ["features-use-limits", targetType, filterFormData],
    () => {
      return DataSvc.getUseLimits({
        keyword: filterFormData.searchKeyword,
        limit: filterFormData.numberPerPage,
        offset: (filterFormData.pageIndex - 1) * filterFormData.numberPerPage,
        target: targetType,
        ...(filterFormData.sort && {
          sort:
            filterFormData.sort === "name"
              ? NAME_SORT_FIELD_BY_TARGET[targetType]
              : filterFormData.sort,
          order: filterFormData.order,
        }),
        ...(filterFormData.useLimitsStatus.length > 0 && {
          conditions: JSON.stringify(
            filterFormData.useLimitsStatus.filter(
              (item: any) => item.can_use !== undefined
            )
          ),
        }),
      });
    }
  );

  const deleteUseLimits = useMutation(() => {
    return DataSvc.deleteUseLimits(
      assignUseLimitData.id,
      assignUseLimitData.target ?? FeatureTypes.ORG
    );
  });

  const [arrangedUseLimitsData, setArrangedUseLimitsData] = useState<{
    arrangedUseLimits: ArrangedUseLimitsData[];
    totalCount: number;
  }>({ arrangedUseLimits: [], totalCount: 0 });

  useEffect(() => {
    if (useLimitsData) {
      const arrangedUseLimits = getArrangedUselimits(
        useLimitsData.list,
        targetType
      );
      setArrangedUseLimitsData({
        arrangedUseLimits: arrangedUseLimits,
        totalCount: useLimitsData.totalCount,
      });
    }
  }, [useLimitsData]);

  const switchingTargetType = (nextTargetType: FeatureTypes) => {
    setTargetType(nextTargetType);
    setFilterFormData(defaultFilterFormData);
    setArrangedUseLimitsData({ arrangedUseLimits: [], totalCount: 0 });
  };

  const tabsData = [FeatureTypes.ORG, FeatureTypes.LOC, FeatureTypes.USER].map(
    (item) => ({
      key: item,
      label: t(`switch_to_${item}_features`),
    })
  );

  const onDeleteUseLimits = async () => {
    const data = await deleteUseLimits.mutateAsync();
    if (data.status === 200) {
      setShowModalConfirmDeleteUseLimits(false);
      setShowModalEditFeatures(false);
      refetchingUseLimits();
    }
  };

  return (
    <div className="right-content function-management">
      <div className="top-title-bar flex-grid">
        <PageTitle text={`${t("function_management")} (${t(targetType)})`} />
        <div className="right-btns">
          <Tabs
            data={tabsData}
            defaultKey={targetType}
            onChanged={(key) => {
              switchingTargetType(key as FeatureTypes);
            }}
          />
        </div>
        <div className="line"></div>
      </div>

      <div className="individual-settings">{t(targetType)}</div>
      {!!arrangedUseLimitsData && (
        <FunctionIndividualSettingList
          arrangedUseLimits={arrangedUseLimitsData.arrangedUseLimits}
          targetType={targetType}
          totalCount={arrangedUseLimitsData.totalCount}
          filterFormData={filterFormData}
          onChangeFilterFormData={(filterFormData: any) => {
            setFilterFormData(filterFormData);
          }}
          onClickEdit={(useLimit: ArrangedUseLimitsData) => {
            setAssignUseLimitData(useLimit);
            setShowModalEditFeatures(true);
          }}
          onClickFilter={() => {
            setShownModalFilterCondition(true);
          }}
          onClickClearFilterCondition={() => {
            setShowModalConfirmClearFilterCondition(true);
          }}
        />
      )}

      {showModalEditFeatures && (
        <ModalEditUseLimit
          arrangeUseLimits={assignUseLimitData}
          onClose={() => {
            setShowModalEditFeatures(false);
          }}
          onRefresh={() => {
            refetchingUseLimits();
          }}
        />
      )}

      {showModalConfirmDeleteUseLimits && (
        <ModalConfirm
          title={"please_confirm"}
          cancelLabel={"cancel"}
          confirmLabel={"confirm"}
          onClose={() => {
            setShowModalConfirmDeleteUseLimits(false);
          }}
          onConfirm={() => {
            onDeleteUseLimits();
            setShowModalConfirmDeleteUseLimits(false);
          }}
        />
      )}

      {showModalConfirmClearFilterCondition && (
        <ModalConfirm
          title={"please_confirm_to_reset"}
          cancelLabel={"cancel"}
          confirmLabel={"confirm"}
          onClose={() => {
            setShowModalConfirmClearFilterCondition(false);
          }}
          onConfirm={() => {
            resetFilterFormData();
            setShowModalConfirmClearFilterCondition(false);
          }}
        />
      )}

      {shownModalFilterCondition && (
        <ModalFilterCondition
          onClose={() => {
            setShownModalFilterCondition(false);
          }}
          filterFormData={filterFormData}
          onApply={(filterFormData: IUseLimitsFilterFormData) => {
            setFilterFormData(filterFormData);
            refetchingUseLimits();
            setShownModalFilterCondition(false);
          }}
        />
      )}
    </div>
  );
};

export default withRouter(FunctionManagementPage);
